// Pure field definitions and validation for the Rates screen (spec section
// 2's table). Field keys match exactly what templates/estimating's Python
// generators and tools/export-rate-catalog.mjs already read/write — this
// module changes the *editor*, never the stored data shape.
//
// Fix round 1 (2026-09-28): the labor_rate and prep_severity_tier basis
// dropdowns are restricted to the only values the pricing engine's
// require_basis() actually accepts in both templates/estimating generators
// (labor_rate.basis === "hour", prep_severity_tier.rate_basis ===
// "linear_ft") — anything else makes both build_custom_line_item.py and
// build_unit_type_floor_plan_a.py sys.exit rather than generate. labor_rate
// also now exposes a task_type-conditional productivity field
// (productivity_sqft_per_hour for "paint", productivity_lf_per_hour for
// "prep"), because both generators' require_field() calls sys.exit without
// one — see getCategoryFields below.

// Final fix wave, item 5: strict parsing. parseFloat accepted any numeric
// prefix, so "1,250" saved as 1 and "38.5abc" as 38.5. A typed value must
// now be a plain decimal (surrounding spaces allowed, optional sign) and
// nothing else. Scientific notation ("1e3"), hex ("0x10") and thousands
// separators ("1,250") are refused on purpose: on a price form they are
// almost always a typo, and refusing is visible where a silent
// reinterpretation is not. A value that is already a number (stored data)
// only has to be finite. Returns the number, or null. A trailing point
// ("38.") is accepted so a half-typed decimal is not flagged while she is
// still typing it.
const PLAIN_DECIMAL = /^[+-]?(\d+\.?\d*|\.\d+)$/;
export function parseStrictNumber(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  const t = v.trim();
  if (!PLAIN_DECIMAL.test(t)) return null;
  const num = Number(t);
  return Number.isFinite(num) ? num : null;
}

export function isPositiveFinite(n) {
  const num = parseStrictNumber(n);
  return num !== null && num > 0;
}

// Final fix wave, item 9: markup percent may be 0 (a legitimate business
// choice, and the template patcher already accepts it), never negative.
export function isNonNegativeFinite(n) {
  const num = parseStrictNumber(n);
  return num !== null && num >= 0;
}

export const CATEGORY_FIELDS = {
  paint_product: [
    { key: "cost_per_gallon", label: "Cost per gallon", type: "number" },
    { key: "coverage_sqft_per_gallon", label: "Coverage (sq ft/gallon/coat)", type: "number" },
    { key: "default_coats", label: "Default coats", type: "number" },
  ],
  labor_rate: [
    { key: "task_type", label: "Task", type: "select", options: ["paint", "prep"] },
    // Restricted to "hour": both generators' cost formulas only know how to
    // price basis="hour" and require_basis() exits on anything else. Kept
    // as a select (not hardcoded) so a future generator update that adds
    // support for another basis only needs its options list extended here.
    { key: "basis", label: "Basis", type: "select", options: ["hour"] },
    { key: "rate", label: "Rate", type: "number" },
  ],
  prep_severity_tier: [
    { key: "rate", label: "Rate", type: "number" },
    // Restricted to "linear_ft" for the same reason as labor_rate.basis
    // above — require_basis() exits on anything else.
    { key: "rate_basis", label: "Unit", type: "select", options: ["linear_ft"] },
    { key: "criteria", label: "Criteria", type: "text" },
  ],
  markup: [
    // allowZero (final fix wave, item 9): 0% is valid here exactly as in
    // src/rateCellPatch.js's "Markup (%):" cell (allowZero there too).
    { key: "percent", label: "Percent", type: "number", allowZero: true },
    { key: "default", label: "Default", type: "boolean" },
  ],
};

// Returns the field list actually in play for one category, given the rest
// of the item's current data. For every category except labor_rate this is
// just CATEGORY_FIELDS[category]. labor_rate additionally requires a
// productivity field once task_type is chosen: both generators' main()
// calls require_field(paint_labor, "productivity_sqft_per_hour", ...) and
// require_field(prep_labor, "productivity_lf_per_hour", ...) and sys.exit
// if either is absent from the matching catalog entry, so the form must
// collect exactly the one key the generator that will read this row's
// task_type is going to look for.
export function getCategoryFields(category, data) {
  const base = CATEGORY_FIELDS[category];
  if (!base) return [];
  if (category !== "labor_rate") return base;
  const taskType = data?.task_type;
  if (taskType === "paint") {
    return [...base, { key: "productivity_sqft_per_hour", label: "Productivity (sq ft per hour)", type: "number" }];
  }
  if (taskType === "prep") {
    return [...base, { key: "productivity_lf_per_hour", label: "Productivity (linear ft per hour)", type: "number" }];
  }
  return base;
}

// Validates a single field's current value against its own rule, given the
// rest of the item's data (needed because labor_rate's field list itself
// depends on task_type — see getCategoryFields). Returns an error string,
// or null when the value is valid. Used both for the Rates form's
// as-you-type inline errors and, below, by validateRateItem at submit time.
// Returns null for a field that isn't in play for this category/data
// combination — callers that already know the category is valid and are
// only iterating that category's own field list never hit that case.
export function validateRateField(category, key, value, data) {
  const fields = getCategoryFields(category, data);
  const f = fields.find((fld) => fld.key === key);
  if (!f) return null;
  if (f.type === "number") {
    if (f.allowZero) return isNonNegativeFinite(value) ? null : `"${f.label}" must be zero or a positive number.`;
    return isPositiveFinite(value) ? null : `"${f.label}" must be a positive number.`;
  }
  if (f.type === "select") {
    return f.options.includes(value) ? null : `"${f.label}" must be one of: ${f.options.join(", ")}.`;
  }
  if (f.type === "text") {
    return value && String(value).trim() ? null : `"${f.label}" is required.`;
  }
  return null; // "boolean" has no invalid state — any truthy/falsy value is fine.
}

// Validates one item's typed fields for its category. Returns an array of
// human-readable error strings — empty means valid. Every numeric field is
// required to be a positive, finite number: a rate of 0 or less, "abc", or
// Infinity is always a data error, never a legitimate price (Review Focus
// item 3 — the estimator using this screen is not a technical user and
// will mistype).
export function validateRateItem(category, data) {
  if (!CATEGORY_FIELDS[category]) return [`Unknown rate category "${category}".`];
  const fields = getCategoryFields(category, data);
  const errors = [];
  for (const f of fields) {
    const err = validateRateField(category, f.key, data?.[f.key], data);
    if (err) errors.push(err);
  }
  return errors;
}

// Converts a form draft's raw field values into the numeric types the
// stored data shape expects. The Rates form keeps numeric fields as raw
// strings while the estimator is typing (a controlled number input that
// parses on every keystroke mangles a partial decimal like "38." into 38,
// which re-renders as "38" and turns a typed "38.5" into "385" — see
// RateCatalog.jsx), so this conversion only happens once, here, at save
// time. Only touches keys that are one of this category's (possibly
// task_type-conditional) numeric fields; every other key on the draft,
// known or not, passes through unchanged. A numeric field whose raw value
// doesn't actually parse to a finite number is left as-is rather than
// coerced to NaN — validateRateItem is what blocks the save in that case,
// this function is never called with data that failed validation.
export function toRateData(category, draft) {
  const fields = getCategoryFields(category, draft);
  const result = { ...draft };
  for (const f of fields) {
    if (f.type !== "number") continue;
    const raw = draft?.[f.key];
    const num = parseStrictNumber(raw);
    result[f.key] = num !== null ? num : raw;
  }
  return result;
}
